import React from 'react';
import { Container } from './Container';
import { firmData } from '../../data/firmData';
import { FOOTER_DISCLAIMER } from '../../constants/disclaimers';
import { NAVIGATION_SECTIONS } from '../../constants/navigation';
import type { NavigationSection } from '../../types/navigation';

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const headOffice = firmData.offices.find(office => office.type === 'head');

  const quickLinks = NAVIGATION_SECTIONS.filter((section: NavigationSection) => 
    ['about', 'practice-areas', 'approach', 'leadership', 'insights', 'contact'].includes(section.id)
  );

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-primary-navy text-white">
      <Container>
        <div className="py-16">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            
            {/* Firm Information */}
            <div className="lg:col-span-1">
              <img
                src="/images/vc-law-logo-dark-bg.png"
                alt={firmData.name}
                className="h-32 w-auto mb-4"
              />
              <h3 className="text-xl font-semibold mb-4 text-accent-gold">
                {firmData.name}
              </h3>
              <p className="text-gray-300 mb-4 leading-relaxed">
                {firmData.tagline}
              </p>
              <p className="text-gray-300 text-sm">
                Established {firmData.foundingYear}
              </p>
              <div className="mt-4">
                <p className="text-gray-300 text-sm">
                  {firmData.statistics.yearsOfPractice}+ Years of Excellence
                </p>
                <p className="text-gray-300 text-sm">
                  {firmData.statistics.mattersHandled}+ Matters Handled
                </p>
              </div>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-1">
              <h4 className="text-lg font-semibold mb-4 text-accent-gold">
                Quick Links
              </h4>
              <nav className="space-y-2">
                {quickLinks.map((link: NavigationSection) => (
                  <button
                    key={link.id}
                    onClick={() => scrollToSection(link.id)}
                    className="block text-gray-300 hover:text-accent-gold transition-colors duration-200 text-left"
                  >
                    {link.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Head Office */}
            <div className="lg:col-span-1">
              <h4 className="text-lg font-semibold mb-4 text-accent-gold">
                Office
              </h4>
              {headOffice && (
                <div className="text-gray-300 space-y-2">
                  <p className="leading-relaxed">
                    {headOffice.address}<br />
                    {headOffice.city}, {headOffice.state} {headOffice.pinCode}
                  </p>
                  {headOffice.phone && (
                    <p>
                      <span className="text-sm text-gray-400">Phone:</span><br />
                      <a 
                        href={`tel:${headOffice.phone}`}
                        className="hover:text-accent-gold transition-colors duration-200"
                      >
                        {headOffice.phone}
                      </a>
                    </p>
                  )}
                  {headOffice.email && (
                    <p>
                      <span className="text-sm text-gray-400">Email:</span><br />
                      <a 
                        href={`mailto:${headOffice.email}`}
                        className="hover:text-accent-gold transition-colors duration-200"
                      >
                        {headOffice.email}
                      </a>
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Disclaimer Section */}
          <div className="border-t border-gray-600 pt-8 mb-8">
            <h4 className="text-lg font-semibold mb-4 text-accent-gold">
              Important Disclaimer
            </h4>
            <div className="text-gray-300 text-sm leading-relaxed space-y-4">
              {FOOTER_DISCLAIMER.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-gray-400 text-sm">
                © {currentYear} {firmData.name}. All rights reserved.
              </div>
              <div className="text-gray-400 text-sm text-center md:text-right">
                <p>This website is in compliance with Rule 49 of the Bar Council of India Rules.</p>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
};